/**
 * OnboardingShell — the authenticated surface for an account with NO voyage.
 *
 * TestFlight 1.0 build 8 (Apple account, no voyage): the header profile button
 * "did not respond". The tap fired `setRoute("settings")`, but this branch
 * rendered <OnboardingWelcome/> regardless of `route` and omits the bottom tab
 * bar, so Settings / Sign out were unreachable without adding a cruise.
 * Account routes now render the Settings workspace here, with a way back.
 */
import React, { Suspense } from "react";
import { AuthenticatedHeader } from "@/components/layout/AuthenticatedHeader";
import { useCruise } from "@/context/CruiseContext";
import { useAuth } from "@/context/AuthContext";

const OnboardingWelcome = React.lazy(() => import("@/pages/OnboardingWelcome"));
const SettingsWorkspace = React.lazy(() => import("@/components/settings/SettingsWorkspace"));

// Mirrors MobileBottomTabBar ME_ALIASES: account/session/preference surfaces.
export const ACCOUNT_ROUTES = new Set(["settings", "shared-with-me", "memories-center"]);

function Fallback() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-live="polite" aria-busy="true">
      <div className="h-8 w-2/3 rounded bg-slate-200/70" />
      <div className="h-56 w-full rounded-2xl bg-slate-100/80" />
      <div className="sr-only">Loading…</div>
    </div>
  );
}

export default function OnboardingShell({ onPlanOwn, onDemoReady }) {
  const { route, setRoute } = useCruise();
  const { user, logout, switchAccount } = useAuth();   // same session actions as SessionCard / AccountMenu
  const onAccount = ACCOUNT_ROUTES.has(route);
  return (
    <div className="min-h-screen bg-[#0b132b]" data-testid="cc-authenticated-onboarding-shell">
      <AuthenticatedHeader
        showNavigation={false}
        onAddCruise={onPlanOwn}
        onBack={onAccount ? () => setRoute("dashboard") : undefined}
      />
      {onAccount ? (
        <div
          className="min-h-screen bg-[#F4F7F9] px-4 pb-[calc(env(safe-area-inset-bottom)+2rem)] pt-3 text-slate-900 sm:px-6"
          data-testid="cc-onboarding-account-surface"
        >
          <Suspense fallback={<Fallback />}>
            <SettingsWorkspace />
          </Suspense>
        </div>
      ) : (
        <Suspense fallback={<Fallback />}>
          <OnboardingWelcome
            onDemoReady={onDemoReady}
            onPlanOwn={onPlanOwn}
            onOpenAccount={() => setRoute("settings")}
            onSignOut={logout}
            onSwitchAccount={switchAccount}
            account={user ? { name: user.display_name || user.name || null, email: user.email || null, provider: user.provider || null } : null}
          />
        </Suspense>
      )}
    </div>
  );
}
